import { router, type Href } from 'expo-router';
import { ChevronLeft } from 'lucide-react-native';
import { Pressable, StyleSheet, View } from 'react-native';

import { Text } from '@/components/Text';
import { colors, radii, spacing } from '@/constants/theme';

type Tone = 'onLight' | 'onDark';

/** The circle variant's diameter: a full 44pt touch target. */
export const BACK_CIRCLE_SIZE = 44;

export type BackChevronProps = {
  /** Defaults to `goBack()`. Override for screens that need a custom back action. */
  onPress?: () => void;
  /** Color context. `onDark` = frost stroke for use inside Deep Green surfaces. */
  tone?: Tone;
  /** `circle`: a 44pt frosted round button, for full-bleed dark headers. */
  variant?: 'bare' | 'circle';
  /** Names where back goes ("View Deal"): a compact "‹ View Deal" text link, as in the Figma. */
  label?: string;
  testID?: string;
};

/**
 * Safe back action. On a cold deep link (URL opened directly, e.g.
 * /deals/<bad-slug> → "Deal not found") the navigation history is
 * empty and router.back() silently no-ops, stranding the user — fall
 * back to `fallback` (home by default) instead. Exported for screens
 * that back-navigate outside this component.
 */
export function goBack(fallback: Href = '/') {
  if (router.canGoBack()) {
    router.back();
  } else {
    router.replace(fallback);
  }
}

/**
 * Back navigation affordance for screens that hide the system header
 * (`<Stack.Screen options={{ headerShown: false }} />`). Use `onLight`
 * when sitting on the frost background, `onDark` when nested inside a
 * Deep Green hero card.
 */
export function BackChevron({ onPress, tone = 'onLight', variant = 'bare', label, testID }: BackChevronProps) {
  const circle = variant === 'circle';
  const strokeColor = tone === 'onDark' ? colors.frost : colors.deepGreen;
  if (label && !circle) {
    // One text-height row instead of a 44pt icon box; hitSlop keeps the 44pt target.
    return (
      <Pressable
        onPress={onPress ?? (() => goBack())}
        hitSlop={LINK_SLOP}
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityHint="Goes back"
        testID={testID}
        style={({ pressed }) => [styles.link, pressed && styles.backPressed]}
      >
        <ChevronLeft size={20} color={strokeColor} strokeWidth={2.25} />
        <Text color={tone === 'onDark' ? 'frost' : 'deepGreen'}>{label}</Text>
      </Pressable>
    );
  }
  return (
    <Pressable
      onPress={onPress ?? (() => goBack())}
      hitSlop={12}
      accessibilityRole="button"
      accessibilityLabel="Back"
      testID={testID}
      style={({ pressed }) => [circle ? styles.circle : styles.back, pressed && styles.backPressed]}
    >
      <View style={circle ? undefined : styles.backInner}>
        <ChevronLeft size={circle ? 22 : 28} color={strokeColor} strokeWidth={2.25} />
      </View>
    </Pressable>
  );
}

/** Grows the ~22pt link row to a 44pt touch target. */
const LINK_SLOP = { top: 11, bottom: 11, left: 12, right: 12 };

const styles = StyleSheet.create({
  link: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  back: {
    alignSelf: 'flex-start',
    marginLeft: -spacing.sm,
  },
  circle: {
    width: BACK_CIRCLE_SIZE,
    height: BACK_CIRCLE_SIZE,
    borderRadius: radii.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.glassFrostFill,
    borderWidth: 1,
    borderColor: colors.glassFrostBorder,
  },
  backPressed: { opacity: 0.6 },
  backInner: { padding: spacing.sm },
});
